import React from "react";
import { CheckCircle } from "@phosphor-icons/react";
import { MaskReveal, FadeUp, Stagger, StaggerItem } from "@/components/Motion";
import { PillButton, Eyebrow, CTASection } from "@/components/Shared";

export default function AudiencePage({ config }) {
  return (
    <div data-testid={`${config.key}-page`}>
      {/* HERO */}
      <section className="relative overflow-hidden bg-white pt-28 sm:pt-36 pb-14 sm:pb-20">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="absolute -top-20 -right-16 h-72 w-72 rounded-full bg-ol-blue/15 blur-3xl" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
          <div>
            <FadeUp><Eyebrow>{config.tagline}</Eyebrow></FadeUp>
            <h1 className="mt-6 text-[2.3rem] leading-[1.02] sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-ol-ink">
              <MaskReveal lines={[config.hero]} delay={0.1} lineClass="text-ol-ink" />
            </h1>
            <div className="mt-6 max-w-xl">
              <p className="text-lg sm:text-xl text-ol-gray leading-relaxed">
                {config.intro}
              </p>
            </div>
            <div className="mt-8">
              <PillButton to="/waitlist" testId={`${config.key}-hero-cta`}>Join the Waitlist</PillButton>
            </div>
          </div>
          <FadeUp delay={0.2} className="relative">
            <div className="relative overflow-hidden rounded-[2rem] soft-shadow">
              <img src={config.image} alt={config.label} className="w-full h-[320px] sm:h-[440px] object-cover" data-testid={`${config.key}-hero-image`} />
              <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-[2rem]" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* STEPS */}
      <section className="px-5 sm:px-8 py-16 sm:py-24 bg-ol-surface" data-testid={`${config.key}-steps`}>
        <div className="mx-auto max-w-6xl">
          <FadeUp className="max-w-2xl">
            <Eyebrow>How it works</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight text-ol-ink leading-[1.08]">
              Four simple steps.
            </h2>
          </FadeUp>
          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2">
            {config.steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <StaggerItem key={i}>
                  <div className="group flex gap-5 rounded-[1.8rem] bg-white p-6 sm:p-8 soft-shadow-sm h-full">
                    <div className="shrink-0">
                      <div className="grid place-items-center h-14 w-14 rounded-2xl bg-ol-blue/10 text-ol-blue">
                        <Icon size={28} weight="duotone" />
                      </div>
                      <div className="mt-3 text-center text-xs font-bold text-ol-blue/50">STEP {i + 1}</div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-ol-ink">{s.title}</h3>
                      <p className="mt-2 text-ol-gray leading-relaxed">{s.text}</p>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* VALUE POINTS */}
      <section className="px-5 sm:px-8 py-16 sm:py-24" data-testid={`${config.key}-values`}>
        <div className="mx-auto max-w-6xl">
          <FadeUp className="max-w-2xl">
            <Eyebrow>Why it matters</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight text-ol-ink leading-[1.08]">
              {config.cta}.
            </h2>
          </FadeUp>
          <Stagger className="mt-12 grid gap-6 md:grid-cols-3">
            {config.values.map((v, i) => {
              const Icon = v.icon;
              return (
                <StaggerItem key={i}>
                  <div className="h-full rounded-[1.8rem] bg-white p-7 soft-shadow-sm border border-gray-50 hover:-translate-y-1 transition-transform duration-300">
                    <div className="grid place-items-center h-12 w-12 rounded-xl bg-ol-blue text-white">
                      <Icon size={24} weight="bold" />
                    </div>
                    <h3 className="mt-5 text-lg font-bold text-ol-ink">{v.title}</h3>
                    <p className="mt-2 text-ol-gray leading-relaxed">{v.text}</p>
                    <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-ol-blue">
                      <CheckCircle size={18} weight="fill" /> Included in OneLease
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <CTASection testId={`${config.key}-cta`} />
    </div>
  );
}
